import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "luxe_access_token";
const REFRESH_KEY = "luxe_refresh_token";

// In-memory fallback for browsers that block localStorage (e.g. third-party iframes / Safari ITP).
let memAccess = null;
let memRefresh = null;

function safeGet(key) {
  try { return localStorage.getItem(key); } catch (e) { return null; }
}
function safeSet(key, val) {
  try { localStorage.setItem(key, val); } catch (e) { /* storage blocked */ }
}
function safeRemove(key) {
  try { localStorage.removeItem(key); } catch (e) { /* storage blocked */ }
}

export const tokenStore = {
  get: () => safeGet(TOKEN_KEY) || memAccess,
  getRefresh: () => safeGet(REFRESH_KEY) || memRefresh,
  set: (access, refresh) => {
    if (access) { memAccess = access; safeSet(TOKEN_KEY, access); }
    if (refresh) { memRefresh = refresh; safeSet(REFRESH_KEY, refresh); }
  },
  clear: () => {
    memAccess = null; memRefresh = null;
    safeRemove(TOKEN_KEY); safeRemove(REFRESH_KEY);
  },
};

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach the stored access token as a Bearer header (works even when cookies are blocked in embedded views).
api.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-refresh the session on 401, then retry the original request once.
let refreshing = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/register") || url.includes("/auth/me");

    if (status === 401 && original && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        if (!refreshing) {
          const rt = tokenStore.getRefresh();
          refreshing = api
            .post("/auth/refresh", {}, { headers: rt ? { "X-Refresh-Token": rt } : {} })
            .then((res) => {
              tokenStore.set(res.data.access_token, res.data.refresh_token);
              return res;
            })
            .finally(() => { refreshing = null; });
        }
        await refreshing;
        return api(original);
      } catch (e) {
        tokenStore.clear();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
